import { Injectable } from '@angular/core';
import { Observable, Subscriber, of, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LanguageService } from '@box-core/services/language.service';

@Injectable({ providedIn: 'root' })
export class PlacesService {
  private readonly mapElement = document.createElement('div');
  private sessionToken: google.maps.places.AutocompleteSessionToken;

  constructor(private languageService: LanguageService) {}

  public getPlacePredictions(input: string): Observable<google.maps.places.AutocompletePrediction[]> {
    if (!this.sessionToken) this.createSessionToken();

    const autocompleteWidget = new google.maps.places.AutocompleteService();
    const request: google.maps.places.AutocompletionRequest = {
      componentRestrictions: { country: 'gr' },
      input,
      language: this.languageService.getLanguageCode(),
      types: ['address'],
      sessionToken: this.sessionToken
    };

    return from(autocompleteWidget.getPlacePredictions(request)).pipe(
      map((response: google.maps.places.AutocompleteResponse) => response?.predictions ?? []),
      catchError(() => of([]))
    );
  }

  public getPlaceDetails(placeId: string): Observable<google.maps.places.PlaceResult> {
    return new Observable((subscriber: Subscriber<google.maps.places.PlaceResult>) => {
      const placesService = new google.maps.places.PlacesService(this.mapElement);
      const request: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields: ['address_components', 'geometry.location'],
        language: this.languageService.getLanguageCode(),
        sessionToken: this.sessionToken
      };

      placesService.getDetails(request, (place, status) => {
        this.clearSessionToken();
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          subscriber.next(place);
          subscriber.complete();
        } else {
          subscriber.error(new Error(`Place Details Request failed with status: ${status}`));
        }
      });
    });
  }

  private createSessionToken(): void {
    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  private clearSessionToken(): void {
    this.sessionToken = null;
  }
}
